import React, { useState, useEffect } from 'react';
import LoadingState from './LoadingState';
import api from '../api';
import { sanitizeHtml } from '../utils/sanitizeHtml';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  docId: 'policy' | 'consent' | 'public-offer' | 'terms';
}

/** Простой форматтер markdown -> HTML (безопасный для доверенного контента) */
function formatMarkdown(text: string): string {
  // Разбиваем на строки и обрабатываем построчно
  const lines = text.split('\n');
  const htmlLines: string[] = [];
  let inList = false;

  for (const line of lines) {
    let processed = line;

    // Списки
    if (/^\- (.+)$/.test(processed)) {
      if (!inList) {
        htmlLines.push('<ul>');
        inList = true;
      }
      const item = processed.replace(/^\- (.+)$/, '$1');
      htmlLines.push(`<li>${applyInlineStyles(item)}</li>`);
      continue;
    } else if (inList) {
      htmlLines.push('</ul>');
      inList = false;
    }

    // Заголовки
    if (/^### (.+)$/.test(processed)) {
      htmlLines.push(`<h3>${applyInlineStyles(processed.slice(4))}</h3>`);
    } else if (/^## (.+)$/.test(processed)) {
      htmlLines.push(`<h2>${applyInlineStyles(processed.slice(3))}</h2>`);
    } else if (/^# (.+)$/.test(processed)) {
      htmlLines.push(`<h1>${applyInlineStyles(processed.slice(2))}</h1>`);
    } else if (processed.trim() === '') {
      htmlLines.push('<br/>');
    } else {
      htmlLines.push(`<p>${applyInlineStyles(processed)}</p>`);
    }
  }

  if (inList) {
    htmlLines.push('</ul>');
  }

  return htmlLines.join('\n');
}

function applyInlineStyles(text: string): string {
  // Сначала **bold**, потом *italic* (порядок важен!)
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/([^\\])\*(.+?)\*/g, '$1<em>$2</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>');
}

const LegalModal: React.FC<LegalModalProps> = ({ isOpen, onClose, docId }) => {
  const [content, setContent] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && docId) {
      setLoading(true);
      setContent('');
      setTitle('');
      api.get<{ title: string; content: string }>(`/legal/${docId}`)
        .then(res => {
          if (res.success && res.data) {
            setTitle(res.data.title);
            const html = formatMarkdown(res.data.content);
            console.log(`[LegalModal] docId=${docId}, content length=${res.data.content.length}, html length=${html.length}`);
            setContent(html);
          } else {
            console.error(`[LegalModal] API returned success=false or no data`);
          }
        })
        .catch(err => console.error('[LegalModal] Failed to load legal doc:', err))
        .finally(() => setLoading(false));
    }
  }, [isOpen, docId]);

  // Закрытие по Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(10px)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 100%)',
          border: '1px solid rgba(212, 175, 55, 0.3)',
          borderRadius: '24px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 32px',
            borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
          }}
        >
          <h2
            style={{
              color: '#d4af37',
              fontSize: '20px',
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              margin: 0,
            }}
          >
            {title || 'Документ'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#d4af37',
              fontSize: '28px',
              cursor: 'pointer',
              padding: '0 8px',
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '32px',
          }}
        >
          {loading ? (
            <LoadingState text="Загрузка документа..." />
          ) : (
            <div
              style={{
                color: '#e0e0e0',
                fontSize: '14px',
                lineHeight: 1.8,
                fontFamily: 'var(--font-sans)',
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
            />
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 32px',
            borderTop: '1px solid rgba(212, 175, 55, 0.2)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
              border: 'none',
              padding: '12px 32px',
              borderRadius: '25px',
              color: '#0a0a1a',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalModal;
